import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Switch } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import axios, { AxiosError, AxiosResponse } from "axios";
import { ICustomer } from "..";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import InputMask from "../../../components/InputMask/Default";
import CnpjClear from "../../../components/InputMask/Default/clear/CnpjClear";
import CepClear from "../../../components/InputMask/Default/clear/CepClear";

interface ICustomerModalProps {
  visible: boolean;
  handleCloseModal: (customer?: ICustomer) => void;
  customer?: ICustomer;
}

interface IInputData {
  neighborhood: string;
  city: string;
  uf: string;
  street: string;
}

interface ICustomerFormData {
  name: string;
  document_number: string;
  generate_purchase_order: boolean;
  cep: string;
  neighborhood: string;
  city: string;
  uf: string;
  street: string;
  number: string;
}

const layout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
};

interface IResponseCep {
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
}

const CustomerModal: React.FC<ICustomerModalProps> = ({
  visible,
  handleCloseModal,
  customer,
}) => {
  const { t } = useTranslation(["customers"]);
  const [loading, setLoading] = useState(false);
  const [inputData, setInputData] = useState<IInputData | null>();
  const [validationErrors, setValidationErrors] = useState({});

  const { addToast } = useToast();
  const [form] = Form.useForm();

  const isEditing = useMemo(() => !!customer, [customer]);

  useEffect(() => {
    if (customer) {
      form.setFieldsValue({
        ...customer,
      });
      setInputData({
        city: customer?.city || "",
        uf: customer?.uf || "",
        neighborhood: customer?.neighborhood || "",
        street: customer?.street || "",
      });
    } else {
      form.setFieldsValue({
        cep: "",
      });
      setInputData(null);
    }
  }, [form, customer, visible]);

  const handleClose = useCallback(
    (cust?: ICustomer) => {
      form.resetFields();
      handleCloseModal(cust);
    },
    [form, handleCloseModal],
  );

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O cliente não foi encontrado";
          break;
        case 409:
          description = "O CNPJ informado já é usado por outro cliente";
          break;
        default:
          description = isEditing
            ? "Ocorreu um erro ao alterar os dados do cliente, tente novamente"
            : "Ocorreu um erro ao criar o cliente, tente novamente";
      }

      addToast({
        type: "error",
        title: isEditing ? "Erro na edição" : "Erro na criação",
        description,
      });
    },
    [addToast, isEditing],
  );

  const handleSubmit = useCallback(
    async (data: ICustomerFormData) => {
      try {
        const schema = Yup.object().shape({
          name: Yup.string().required("Nome obrigatório"),
          document_number: Yup.string().required("CNPJ obrigatório"),
          generate_purchase_order: Yup.boolean(),
          cep: Yup.string()
            .required("CEP é obrigatório")
            .min(8, "cep incompleto"),
          neighborhood: Yup.string().required("Bairro é Obrigatório"),
          city: Yup.string().required("Cidade é Obrigatória"),
          uf: Yup.string().required("UF é Obrigatório"),
          street: Yup.string().required("Rua é Obrigatória"),
          number: Yup.string().required("Numero é Obrigatório"),
        });

        await schema.validate(
          {
            ...data,
            cep: CepClear(data?.cep),
            document_number: CnpjClear(data?.document_number),
          },
          {
            abortEarly: false,
          },
        );

        setLoading(true);

        const {
          name,
          document_number,
          generate_purchase_order,
          cep,
          number,
          street,
          uf,
          neighborhood,
          city,
        } = data;

        const payload = {
          name,
          generate_purchase_order,
          cep: CepClear(cep),
          document_number: CnpjClear(document_number),
          number,
          street,
          uf,
          neighborhood,
          city,
        };

        let response: AxiosResponse;
        if (isEditing) {
          response = await api.put(`/customers/${customer?.id}`, payload);
          addToast({
            type: "success",
            title: "Cliente alterado",
            description: "O cliente foi alterado com sucesso",
          });
        } else {
          response = await api.post("/customers", payload);
          addToast({
            type: "success",
            title: "Cliente criado",
            description: "O cliente foi criado com sucesso",
          });
        }
        handleClose(response.data);
      } catch (err) {
        console.log({ err });
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, customer?.id, generateErrorToast, handleClose, isEditing],
  );

  const getCEP = async (cep: string) => {
    const clearCEP = cep.replace("-", "").replace(".", "").replace(/_/g, "");
    if (clearCEP.length < 8) setInputData(() => null);
    if (clearCEP.length !== 8) return;
    try {
      const getAddress: AxiosResponse<IResponseCep> = await axios.get(
        `https://viacep.com.br/ws/${clearCEP}/json`,
      );
      const add = getAddress.data;
      const data = {
        neighborhood: String(add.bairro || ""),
        city: String(add.localidade || ""),
        uf: String(add.uf || ""),
        street: String(add.logradouro || ""),
      } as IInputData;
      form.setFieldsValue(data);
      setInputData(data);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Modal
      title={isEditing ? t("actions.edit") : t("actions.create")}
      visible={visible}
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      destroyOnClose
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem name="name" required label="Nome" errors={validationErrors}>
          <Input />
        </FormItem>
        <FormItem
          name="document_number"
          required
          label="CNPJ"
          errors={validationErrors}
        >
          <InputMask
            mask="99.999.999/9999-99"
            maskChar="_"
            err={!!((validationErrors as unknown) as any)?.document_number}
          />
        </FormItem>
        <FormItem
          name="cep"
          required
          label="CEP"
          errors={validationErrors}
          initialValue={customer?.cep || ""}
        >
          <InputMask
            mask="99.999-999"
            maskChar="_"
            onChange={(e: any) => getCEP(e.target.value)}
            err={!!((validationErrors as unknown) as any)?.cep}
          />
        </FormItem>

        {inputData && (
          <>
            <FormItem
              name="neighborhood"
              required
              label="Bairro"
              key="neighborhood"
              errors={validationErrors}
              initialValue={inputData.neighborhood}
            >
              <Input disabled={!!inputData.neighborhood} />
            </FormItem>
            <FormItem
              name="city"
              required
              label="Cidade"
              key="city"
              errors={validationErrors}
              initialValue={inputData.city}
            >
              <Input disabled={!!inputData.city} />
            </FormItem>
            <FormItem
              name="uf"
              required
              label="UF"
              key="uf"
              errors={validationErrors}
              initialValue={inputData.uf}
            >
              <Input disabled={!!inputData.uf} />
            </FormItem>
            <FormItem
              name="street"
              required
              label="Rua"
              key="street"
              errors={validationErrors}
              initialValue={inputData.street}
            >
              <Input disabled={!!inputData.street} />
            </FormItem>
            <FormItem
              name="number"
              required
              label="nº"
              key="number"
              errors={validationErrors}
            >
              <Input />
            </FormItem>
          </>
        )}
        {/* <FormItem
          name="folha_de_servico"
          label="Folha de Serviços"
          errors={validationErrors}
        >
          <Input />
        </FormItem> */}
        <FormItem
          name="service_sheet"
          label="Folha de Serviço"
          valuePropName="checked"
          errors={validationErrors}
        >
          <Switch />
        </FormItem>
        <FormItem
          name="generate_purchase_order"
          label="Gera ordem de compra"
          valuePropName="checked"
          errors={validationErrors}
        >
          <Switch />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default CustomerModal;
